"use client"

import React, { createContext, useContext, ReactNode, useMemo } from "react"
import { useSession } from "next-auth/react"
import type { Session, User } from "next-auth"
import { useTokenRefresh } from "../hooks/useTokenRefresh"

interface TokenDebugInfo {
  isExpired: boolean
  expiresAt?: string
  issuedAt?: string
  secondsUntilExpiry?: number
  tokenAge?: number
}

interface AuthContextType {
  session: Session | null
  isAuthenticated: boolean
  isTokenExpired: boolean
  user: User | null
  isLoading: boolean
  hasRefreshError: boolean
  refreshToken: () => Promise<boolean>
  getTokenDebug: () => TokenDebugInfo | null
}

// Utility functions to calculate token timing
export function calculateTokenExpiry(expiresAt?: number): { isExpired: boolean; secondsUntilExpiry: number } {
  if (typeof expiresAt !== "number") {
    return { isExpired: false, secondsUntilExpiry: 0 }
  }

  const now = Math.floor(Date.now() / 1000)
  const secondsUntilExpiry = expiresAt - now
  const isExpired = secondsUntilExpiry <= 0

  return { isExpired, secondsUntilExpiry }
}

export function calculateTokenAge(issuedAt?: number): number {
  if (typeof issuedAt !== "number") {
    return 0
  }

  const now = Math.floor(Date.now() / 1000)
  return now - issuedAt
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { data: session, status } = useSession()
  const { refresh } = useTokenRefresh()
  const isLoading = status === "loading"

  // session.user is automatically undefined when token expired (handled in auth.ts)
  const isAuthenticated = !!session?.user
  const { isExpired: isTokenExpired } = calculateTokenExpiry(session?.tokens?.expiresAt)
  const hasRefreshError = session?.error === "RefreshAccessTokenError"

  // Pass through session user directly (already has correct type)
  const user: User | null = session?.user || null

  const getTokenDebug = useMemo(() => {
    return (): TokenDebugInfo | null => {
      if (!session?.tokens) return null

      const { isExpired, secondsUntilExpiry } = calculateTokenExpiry(session.tokens.expiresAt)
      const tokenAge = calculateTokenAge(session.tokens.issuedAt)

      return {
        isExpired,
        expiresAt: new Date(session.tokens.expiresAt * 1000).toISOString(),
        issuedAt: new Date(session.tokens.issuedAt * 1000).toISOString(),
        secondsUntilExpiry,
        tokenAge,
      }
    }
  }, [session?.tokens])

  const contextValue: AuthContextType = useMemo(
    () => ({
      session,
      isAuthenticated,
      isTokenExpired,
      user,
      isLoading,
      hasRefreshError,
      refreshToken: refresh,
      getTokenDebug,
    }),
    [session, isAuthenticated, isTokenExpired, user, isLoading, hasRefreshError, refresh, getTokenDebug]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
